import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowUp, Loader2, AlertTriangle, Database, Plus, Bookmark, Trash2, MessageSquare, Search, RefreshCw, Square, Zap, Brain,
  Factory, Briefcase, CircleDollarSign, Gavel, Target, Truck, PackageCheck, Hourglass, Users,
  ClipboardList, MailWarning, CalendarClock, Phone, TrendingUp, ChevronDown,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AnalystGlyph from "./AnalystGlyph";
import AnswerView from "./AnswerView";
import { readSse } from "./answer";
import "./analyst.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHIP_ICONS = {
  money_list: CircleDollarSign, mgmt_decisions: Gavel, month_forecast: Target, procurement_chase: Truck,
  ready_to_ship: PackageCheck, stuck_longest: Hourglass, who_did_what: Users, status_sheet: ClipboardList,
  late_update: MailWarning, deliveries_30: CalendarClock, expedite_list: Zap, call_brief: Phone, ontime_record: TrendingUp,
};
// Suggestion tints — RockLink's own meaning colours (stage hues, blocked rose, money green).
const CHIP_TINT = {
  money_list: "158 64% 40%", mgmt_decisions: "350 75% 55%", month_forecast: "217 91% 60%",
  procurement_chase: "25 95% 53%", ready_to_ship: "38 92% 50%", stuck_longest: "271 91% 65%",
  who_did_what: "172 66% 42%", status_sheet: "217 91% 60%", late_update: "350 75% 55%",
  deliveries_30: "38 92% 50%", call_brief: "172 66% 42%", ontime_record: "158 64% 40%",
};
const MODEL_LOOK = {
  haiku: { name: "Haiku", cls: "text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/30" },
  sonnet: { name: "Sonnet", cls: "text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-500/10 dark:border-orange-500/30" },
  opus: { name: "Opus", cls: "text-violet-700 bg-violet-50 border-violet-200 dark:text-violet-300 dark:bg-violet-500/10 dark:border-violet-500/30" },
};
const COMMENTARY = {
  haiku: "Using Haiku — quick lookup.",
  sonnet: "Using Sonnet — multi-step analysis, allow about 30 seconds.",
  opus: "Using Opus — deep thinking, expect 2–3 minutes.",
};
const fmt = (n) => (n == null ? "" : Number(n).toLocaleString("en-IN"));
const when = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function AskBox({ value, onChange, onSend, onStop, busy, disabledReason, dataLine, big, placeholder, status }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, 220)}px`; }
  }, [value]);
  // The landing box takes the cursor as soon as Analyst opens (and after New question).
  useEffect(() => { if (big && !disabledReason) ref.current?.focus({ preventScroll: true }); }, [big, disabledReason]);
  const blocked = !!disabledReason;
  return (
    <div className={`rounded-2xl border bg-[hsl(var(--card))] transition-colors ${blocked ? "border-[hsl(var(--border))]" : "border-orange-300/70 dark:border-orange-500/30 focus-within:border-orange-500/70 focus-within:shadow-[0_0_0_3px_rgba(249,115,22,0.12)]"} ${big ? "p-4" : "p-3"}`}
      data-testid="analyst-askbox">
      <textarea ref={ref} value={value} onChange={(e) => onChange(e.target.value)} rows={big ? 2 : 1} maxLength={4000}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
        placeholder={disabledReason || placeholder} disabled={busy || blocked}
        className={`w-full resize-none bg-transparent outline-none placeholder:text-[hsl(var(--muted-foreground))]/80 ${big ? "text-[15px] min-h-[52px]" : "text-[14px]"}`}
        data-testid="analyst-input" />
      <div className="flex items-center gap-2 mt-1.5">
        <span className="flex items-center gap-1.5 text-[11.5px] an-muted min-w-0 truncate" data-testid="analyst-dataline">
          <Database className="w-3.5 h-3.5 shrink-0" />{dataLine}
        </span>
        {busy && onStop ? (
          <button type="button" onClick={onStop} data-testid="analyst-stop" aria-label="Stop" title="Stop — you only pay for what has run so far"
            className="ml-auto flex items-center justify-center w-9 h-9 rounded-xl bg-[hsl(var(--foreground))] text-[hsl(var(--background))] hover:opacity-85 active:scale-95 transition shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60">
            <Square className="w-3.5 h-3.5 fill-current" />
          </button>
        ) : (
          <button type="button" onClick={onSend} disabled={busy || blocked || !value.trim()} data-testid="analyst-send"
            aria-label="Ask"
            className="rl-lit-btn ml-auto flex items-center justify-center w-9 h-9 rounded-xl bg-[#f97316] text-white disabled:opacity-40 disabled:shadow-none hover:bg-orange-600 transition-colors shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
          </button>
        )}
      </div>
      {status && (
        <div className="an-swap mt-1.5 flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-[11.5px] an-muted" data-testid="analyst-route">
          <span>
            <span className="font-semibold text-[hsl(var(--foreground))]/75">Model: {status.name}</span>
            {status.deepOffer ? <> · especially complex question — Opus can go deeper</> : status.note && <> · {status.note}</>}
          </span>
          {status.deepOffer && (
            <button type="button" onClick={status.deepOffer.onClick} data-testid="analyst-deep-live"
              title={`${status.deepOffer.reason ? `Why: ${status.deepOffer.reason}. ` : ""}Opus thinks harder — ${status.deepOffer.range}, 2–3 minutes. Switching stops Sonnet now; you only pay for what has run.`}
              className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-violet-300/70 bg-violet-50 text-violet-800 hover:bg-violet-100 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-200 dark:hover:bg-violet-500/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60">
              <Brain className="w-3.5 h-3.5 shrink-0" />
              <span className="font-semibold leading-none">Switch to Opus</span>
              <span className="leading-none opacity-75">· {status.deepOffer.range}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ClientChip({ chip, clients, onPick, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const Icon = CHIP_ICONS[chip.key] || ClipboardList;
  const gold = chip.key === "expedite_list";   // RockLink's Expedite = premium gold
  const list = clients.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={`analyst-chip-${chip.key}`}
          style={gold ? undefined : { "--c": CHIP_TINT[chip.key] || "25 95% 53%" }}
          className={`rl-tile inline-flex items-center gap-2 h-9 pl-3 pr-2.5 rounded-full border text-[12.5px] font-medium disabled:opacity-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 ${
            gold ? "an-gold-chip" : "an-tint-chip"}`}>
          <Icon className="w-4 h-4 an-tint-icon" />{chip.label}
          <span className="inline-flex items-center gap-0.5 text-[11px] an-muted">· choose client<ChevronDown className="w-3 h-3" /></span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2" data-testid="analyst-client-picker">
        <div className="flex items-center gap-2 px-2 h-8 rounded-lg border border-[hsl(var(--border))] mb-1.5">
          <Search className="w-3.5 h-3.5 an-muted" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client"
            className="flex-1 bg-transparent text-[12.5px] outline-none" data-testid="analyst-client-search" />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {list.length === 0 && <p className="px-2 py-3 text-[12px] an-muted">No client matches “{q}”.</p>}
          {list.map((c) => (
            <button key={c.name} type="button" onClick={() => { setOpen(false); onPick(c.name); }}
              className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left text-[12.5px] hover:bg-[hsl(var(--muted))]"
              data-testid={`analyst-client-${c.name}`}>
              <span className="truncate">{c.name}</span>
              <span className="font-mono-data text-[11px] an-muted shrink-0">{c.open_items} open</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Suggestions({ chips, clients, onRun, disabled }) {
  const internal = chips.filter((c) => c.group === "internal");
  const customer = chips.filter((c) => c.group === "customer");
  const plain = "rl-tile inline-flex items-center gap-2 h-9 px-3 rounded-full border text-[12.5px] font-medium disabled:opacity-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60";
  return (
    <div className="space-y-5" style={{ fontFamily: READING_FONT }} data-testid="analyst-suggestions">
      <section>
        <h2 className="flex items-center gap-1.5 text-[12px] font-semibold an-muted mb-2.5">
          <Factory className="w-4 h-4" />For internal
        </h2>
        <div className="flex flex-wrap gap-2">
          {internal.map((c) => {
            const Icon = CHIP_ICONS[c.key] || ClipboardList;
            return (
              <button key={c.key} type="button" disabled={disabled} onClick={() => onRun({ chip: c.key })} className={`${plain} an-tint-chip`}
                style={{ "--c": CHIP_TINT[c.key] || "25 95% 53%" }} data-testid={`analyst-chip-${c.key}`}>
                <Icon className="w-4 h-4 an-tint-icon" />{c.label}
              </button>
            );
          })}
        </div>
      </section>
      <section>
        <h2 className="flex flex-wrap items-center gap-1.5 text-[12px] font-semibold an-muted mb-2.5">
          <Briefcase className="w-4 h-4" />For customers
          <span className="font-normal">· customer-safe: no values, costs or internal notes</span>
        </h2>
        <div className="flex flex-wrap gap-2">
          {customer.map((c) => (
            <ClientChip key={c.key} chip={c} clients={clients} disabled={disabled} onPick={(client) => onRun({ chip: c.key, client })} />
          ))}
        </div>
      </section>
    </div>
  );
}

// Working state: a list that grows step by step. Each step carries the
// Analyst double-chevron; the current one pulses, finished ones go quiet.
function WorkList({ steps, hasText }) {
  const items = [];
  for (const s of steps || []) if (items[items.length - 1] !== s.text) items.push(s.text);
  if (!items.length) items.push("Preparing your data…");
  if (hasText) items.push("Writing the answer…");
  return (
    <ol className="space-y-1.5" aria-live="polite" data-testid="analyst-steps">
      {items.map((t, i) => {
        const current = i === items.length - 1;
        return (
          <li key={i} className={`an-swap flex items-center gap-2 text-[12.5px] ${current ? "font-medium" : "an-muted"}`}>
            <span className={`an-chev ${current ? "an-chev-live" : ""}`}><AnalystGlyph size={12} /></span>
            {current ? t : t.replace(/…$/, "")}
          </li>
        );
      })}
    </ol>
  );
}

// Rupees to the nearest whole number (3.2 → 3, 3.5 → 4); under ₹0.50 reads "≈₹0" (exact $ on hover).
const inr = (usd, rate) => {
  const r = Math.round(usd * rate);
  return r < 1 ? "≈₹0" : `₹${r.toLocaleString("en-IN")}`;
};

function MetaLine({ conv, m, fresh, inrPerUsd }) {
  const c = conv?.counts || {};
  const left = [
    conv?.as_of && `${fmt(c.lines)} lines${conv.audience === "internal" ? ` · ${fmt(c.events)} history events` : ""} · as of ${conv.as_of}`,
    m.codeRuns ? `${m.codeRuns} calculation${m.codeRuns === 1 ? "" : "s"}` : "",
  ].filter(Boolean);
  const model = m.route ? (MODEL_LOOK[m.route.model] || MODEL_LOOK.sonnet).name : "";
  const usd = m.cost != null ? `$${Number(m.cost).toFixed(m.cost < 0.01 ? 4 : 2)}` : "";
  if (!left.length && !model && !usd) return null;
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] an-muted ${fresh ? "an-in" : ""}`} data-testid="analyst-meta">
      {left.length > 0
        ? <span className="flex items-center gap-1.5 min-w-0"><Database className="w-3.5 h-3.5 shrink-0" />{left.join(" · ")}</span>
        : <span>Quick lookup · live RockLink record</span>}
      <span className="ml-auto flex items-center gap-1.5 whitespace-nowrap">
        {model && <span>Model: {model}</span>}
        {model && usd && <span aria-hidden="true">·</span>}
        {usd && (
          <span className="font-mono-data font-semibold text-[hsl(var(--foreground))] cursor-help" data-testid="analyst-cost"
            title={inrPerUsd ? `${usd} — converted at ₹${inrPerUsd} per $1 (Stock Center exchange rate)` : usd}>
            {inrPerUsd ? inr(Number(m.cost), inrPerUsd) : usd}
          </span>
        )}
      </span>
    </div>
  );
}

// After a Sonnet answer the router judged deep-thinking material: offer Opus, never auto-run it.
function DeepOffer({ cost, range, onDeep, busy }) {
  return (
    <div className="an-in flex flex-wrap items-center gap-3 rounded-xl border border-violet-300/60 bg-violet-50/70 dark:border-violet-500/30 dark:bg-violet-500/10 px-3.5 py-2.5" data-testid="analyst-deep-offer">
      <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-500/15 text-violet-700 dark:text-violet-300 shrink-0"><Brain className="w-4 h-4" /></span>
      <p className="flex-1 min-w-[220px] text-[12.5px] leading-snug text-violet-950 dark:text-violet-100">
        <span className="font-semibold">This question deserves deeper thinking.</span>{" "}
        Sonnet answered{cost ? ` for ${cost}` : ""}. Opus, the most capable model here, digs further — {range} and 2–3 minutes.
      </p>
      <button type="button" onClick={onDeep} disabled={busy} data-testid="analyst-deep-run"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60 focus-visible:ring-offset-1">
        <Brain className="w-4 h-4" />Think deeper with Opus
      </button>
    </div>
  );
}

const timeOnly = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };

function TurnHeader({ m, conv, showAudience }) {
  return (
    <div className={m.fresh ? "an-in" : ""} data-testid="analyst-question">
      <p className="flex flex-wrap items-center gap-2 text-[11.5px] font-semibold an-muted">
        <span>You asked{m.at ? ` · ${timeOnly(m.at)}` : ""}</span>
        {m.deep && (
          <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full border border-violet-300 bg-violet-50 text-violet-800 text-[11px] dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-200">
            <Brain className="w-3 h-3" />Deep thinking · Opus
          </span>
        )}
        {showAudience && conv?.audience === "customer" && (
          <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full border border-sky-300 bg-sky-50 text-sky-800 text-[11px] dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200">
            <Briefcase className="w-3 h-3" />For customer · {conv.client}
          </span>
        )}
      </p>
      <h2 className="mt-1 text-[20px] font-bold tracking-tight leading-snug">{m.text}</h2>
    </div>
  );
}

// "Save as report" reward: a bookmark arcs from the button into Saved reports.
function flyTo(fromEl, toSelector) {
  return new Promise((resolve) => {
    const target = document.querySelector(toSelector);
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || document.documentElement.dataset.flat === "1";
    if (!fromEl || !target || !target.offsetParent || still || !document.body.animate) { resolve(); return; }
    const a = fromEl.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const el = document.createElement("div");
    el.className = "an-flyer";
    el.style.left = "0px"; el.style.top = "0px";
    el.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>';
    document.body.appendChild(el);
    const x0 = a.left + 10, y0 = a.top + 7, x1 = b.left + 26, y1 = b.top + 12;
    const anim = el.animate([
      { transform: `translate(${x0}px, ${y0}px) scale(1)` },
      { transform: `translate(${(x0 + x1) / 2}px, ${Math.min(y0, y1) - 90}px) scale(1.5)`, offset: 0.45 },
      { transform: `translate(${x1}px, ${y1}px) scale(0.7)`, opacity: 0.3 },
    ], { duration: 760, easing: "cubic-bezier(.22,.7,.3,1)" });
    anim.onfinish = () => { el.remove(); resolve(); };
  });
}

// Anthropic's clay — the "Powered by Anthropic" mark only (text, no logo).
const READING_FONT = "'Literata', Georgia, serif";

function Tile({ icon: Icon, tint, title, meta, active, fresh, onClick, onDelete, testid, deleteLabel }) {
  return (
    <div className={`group relative rounded-xl border transition-all ${fresh ? "an-in" : ""} ${fresh
      ? "border-emerald-300 bg-emerald-50/60 dark:border-emerald-500/40 dark:bg-emerald-500/10"
      : active
        ? "an-pressed border-transparent"
        : "border-[hsl(var(--border))]/60 bg-[hsl(var(--card))]/60 hover:bg-[hsl(var(--card))] hover:border-[hsl(var(--border))] hover:shadow-[0_2px_6px_rgba(40,30,20,0.08)]"}`}>
      <button type="button" onClick={onClick} data-testid={testid}
        className="flex items-start gap-2.5 w-full px-2.5 py-2 pr-8 text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60">
        <span className={`an-ico flex items-center justify-center w-7 h-7 rounded-lg shrink-0 ${tint}`}><Icon className="w-3.5 h-3.5" /></span>
        <span className="min-w-0">
          <span className={`flex items-center gap-1.5 text-[12.5px] leading-snug ${active ? "font-semibold" : "font-medium"}`}>
            {fresh && <span className="an-fresh shrink-0" aria-hidden="true" />}<span className="truncate">{title}</span>
          </span>
          <span className="block truncate text-[11px] an-muted mt-0.5">{meta}</span>
        </span>
      </button>
      <button type="button" onClick={onDelete} aria-label={deleteLabel}
        className="absolute right-1.5 top-2 p-1 rounded-md opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto an-muted hover:text-red-500 hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 transition-opacity">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Saved report: the tile opens the saved answer; the explicit button re-runs it.
function ReportTile({ r, fresh, active, rerunning, busy, onOpen, onRerun, onDelete }) {
  return (
    <div className={`group relative rounded-xl border transition-all ${fresh ? "an-in border-emerald-300 bg-emerald-50/60 dark:border-emerald-500/40 dark:bg-emerald-500/10"
      : active ? "an-pressed border-transparent"
      : "border-[hsl(var(--border))]/60 bg-[hsl(var(--card))]/60 hover:bg-[hsl(var(--card))] hover:border-[hsl(var(--border))] hover:shadow-[0_2px_6px_rgba(40,30,20,0.08)]"}`}>
      <button type="button" onClick={onOpen} data-testid={`analyst-report-${r.id}`} title="Open the saved answer"
        className="flex items-start gap-2.5 w-full px-2.5 pt-2 pb-1 pr-8 text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60">
        <span className="an-ico flex items-center justify-center w-7 h-7 rounded-lg shrink-0 bg-orange-500/10 text-orange-600 dark:text-orange-400"><Bookmark className="w-3.5 h-3.5" /></span>
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-[12.5px] font-medium leading-snug">
            {fresh && <span className="an-fresh shrink-0" aria-hidden="true" />}<span className="truncate">{r.title}</span>
          </span>
          <span className="block truncate text-[11px] an-muted mt-0.5">
            {r.audience === "customer" ? `Customer · ${r.client} · ` : ""}{r.last_run_at ? `Answer from ${when(r.last_run_at)}` : "Saved"}
          </span>
        </span>
      </button>
      <button type="button" onClick={onRerun} disabled={busy} data-testid={`analyst-rerun-${r.id}`}
        className="ml-[46px] mb-2 inline-flex items-center gap-1.5 h-6 px-2 rounded-md text-[11.5px] font-semibold text-orange-700 dark:text-orange-300 bg-orange-500/10 hover:bg-orange-500/20 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 transition-colors">
        <RefreshCw className={`w-3.5 h-3.5 ${rerunning ? "animate-spin" : ""}`} />
        {rerunning ? "Re-running on fresh data…" : "Re-run on fresh data"}
      </button>
      <button type="button" onClick={onDelete} aria-label={`Delete saved report ${r.title}`}
        className="absolute right-1.5 top-2 p-1 rounded-md opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto an-muted hover:text-red-500 hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 transition-opacity">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function LeftColumn({ reports, convs, activeId, freshReportId, rerunId, overview, onLanding, onNew, onOpenReport, onRerunReport, onDeleteReport, onOpen, onDeleteConv, busy }) {
  return (
    <aside className="lg:w-[260px] shrink-0 lg:self-start" data-testid="analyst-rail">
      <h1 className="flex items-center gap-2.5 text-[22px] font-bold tracking-tight leading-none pt-1">
        <span className="flex items-center justify-center w-8 h-8 rounded-xl bg-orange-500/10 text-orange-600 dark:text-orange-400"><AnalystGlyph size={16} /></span>
        <span className="whitespace-nowrap">
          Analyst
          <sup className="an-badge ml-2" data-testid="analyst-powered-by">
            Powered by Anthropic
          </sup>
        </span>
      </h1>
      <p className="mt-3.5 text-[12.5px] leading-relaxed an-muted">
        Ask anything about your orders. Get clear answers, tables and reports ready to send.
      </p>

      <button type="button" onClick={onNew} disabled={busy} data-testid="analyst-new"
        aria-pressed={onLanding}
        // Stays pressed in while you're on a fresh question — like the header Analyst button on /analyst.
        className={`mt-4 w-full flex items-center gap-2.5 h-11 pl-2 pr-3 rounded-xl text-[13px] font-semibold disabled:opacity-60 transition-[colors,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 focus-visible:ring-offset-2 ${
          onLanding
            ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
            : "rl-search-glass an-new text-white"}`}>
        <span className={`flex items-center justify-center w-7 h-7 rounded-lg ${onLanding ? "bg-[hsl(var(--card))] text-orange-600 dark:text-orange-400" : "bg-white/20"}`}><Plus className="w-4 h-4" /></span>
        New question
      </button>

      <div className="hidden lg:block">
        <div data-testid="analyst-saved-list">
          <p className="mt-5 mb-1.5 px-1 text-[11.5px] font-semibold an-muted">Saved reports</p>
          {reports.length === 0
            ? <p className="px-1 text-[11.5px] leading-relaxed an-muted">Save any answer — it re-runs on fresh data when you open it.</p>
            : <div className="space-y-1">{reports.map((r) => (
                <ReportTile key={r.id} r={r} fresh={r.id === freshReportId} active={!!r.conversation_id && r.conversation_id === activeId}
                  rerunning={rerunId === r.id} busy={busy} onOpen={() => onOpenReport(r)} onRerun={() => onRerunReport(r)}
                  onDelete={() => onDeleteReport(r)} />
              ))}</div>}
        </div>

        {overview && overview.month_cost_usd != null && (
          <div className="mt-5 px-2.5 py-2 rounded-xl bg-[hsl(var(--muted))]/50 text-[11.5px] an-muted" data-testid="analyst-month-spend"
            title={`$${Number(overview.month_cost_usd).toFixed(2)} this month${overview.inr_per_usd ? ` at ₹${overview.inr_per_usd} per $1` : ""}`}>
            <span className="font-semibold">This month</span> · {overview.month_questions} question{overview.month_questions === 1 ? "" : "s"} ·{" "}
            <span className="font-mono-data font-semibold text-[hsl(var(--foreground))]">
              {overview.inr_per_usd ? inr(overview.month_cost_usd, overview.inr_per_usd) : `$${Number(overview.month_cost_usd).toFixed(2)}`}
            </span>
          </div>
        )}
        <p className="mt-3 mb-1.5 px-1 text-[11.5px] font-semibold an-muted">Recent</p>
        {convs.length === 0
          ? <p className="px-1 text-[11.5px] an-muted">Your questions appear here.</p>
          : <div className="space-y-1 max-h-[46vh] overflow-y-auto pr-0.5">{convs.map((c) => (
              <Tile key={c.id} active={c.id === activeId}
                icon={c.audience === "customer" ? Briefcase : MessageSquare}
                tint={c.audience === "customer" ? "bg-sky-500/10 text-sky-700 dark:text-sky-300" : "bg-[hsl(var(--muted))] an-muted"}
                title={c.title} meta={`${c.audience === "customer" ? "Customer" : "Internal"} · ${when(c.updated_at)}`}
                onClick={() => onOpen(c.id)} onDelete={() => onDeleteConv(c)} testid={`analyst-conv-${c.id}`} deleteLabel={`Delete conversation ${c.title}`} />
            ))}</div>}
      </div>
      <p className="mt-4 px-1 text-[10.5px] leading-relaxed an-muted opacity-80" data-testid="analyst-audit-notice">
        Questions are kept for 180 days so admins can review how Analyst is used.
      </p>
    </aside>
  );
}

export default function AnalystPage() {
  const [overview, setOverview] = useState(null);
  const [chips, setChips] = useState([]);
  const [clients, setClients] = useState([]);
  const [reports, setReports] = useState([]);
  const [convs, setConvs] = useState([]);
  const [conv, setConv] = useState(null);          // {id, title, audience, client, as_of, counts}
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshReportId, setFreshReportId] = useState(null);
  const [rerunId, setRerunId] = useState(null);
  const turnRef = useRef(null);
  const abortRef = useRef(null);

  const refreshLists = useCallback(async () => {
    const [r, c] = await Promise.allSettled([axios.get(`${API}/ai/reports`), axios.get(`${API}/ai/conversations`)]);
    if (r.status === "fulfilled") setReports(r.value.data);
    if (c.status === "fulfilled") setConvs(c.value.data);
  }, []);
  const refreshOverview = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/ai/overview`); setOverview(data); } catch (e) {
      setOverview({ error: e?.response?.status === 403 ? "Analyst isn't switched on for your role." : "Couldn't reach Analyst." });
    }
  }, []);

  useEffect(() => {
    // Switching chats changes the page height a lot; the browser's scroll anchoring
    // then drags the page around for a few frames. Off while on Analyst.
    const root = document.documentElement;
    const prev = root.style.overflowAnchor;
    root.style.overflowAnchor = "none";
    return () => { root.style.overflowAnchor = prev; };
  }, []);

  useEffect(() => {
    // Analyst-only reading face (RockLink's own fonts stay app-wide).
    if (!document.getElementById("analyst-font")) {
      const l = document.createElement("link");
      l.id = "analyst-font"; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Literata:opsz,wght@7..72,400;7..72,500;7..72,600;7..72,700&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    refreshOverview();
    refreshLists();
    axios.get(`${API}/ai/chips`).then(({ data }) => setChips(data)).catch(() => {});
    axios.get(`${API}/ai/clients`).then(({ data }) => setClients(data)).catch(() => {});
  }, [refreshOverview, refreshLists]);

  const patchLast = (fn) => setMessages((ms) => { const c = [...ms]; c[c.length - 1] = fn(c[c.length - 1]); return c; });

  const stop = () => abortRef.current?.abort();
  // Every view switch starts at the top, instantly — no clamped or animated jumps.
  const toTop = () => window.scrollTo({ top: 0, behavior: "instant" });
  const newQuestion = () => { if (!busy) { setConv(null); setMessages([]); setInput(""); toTop(); } };

  const run = async ({ message, chip, client, deep = false }) => {
    const text = (message ?? "").trim();
    if (busy || (!text && !chip)) return null;
    let newConvId = null;
    const chipDef = chips.find((c) => c.key === chip);
    const label = chipDef ? (client ? `${chipDef.label} — ${client}` : chipDef.label) : text;
    const followUp = !!conv && !chip;
    if (!followUp) setConv(null);
    setInput("");
    setBusy(true);
    setMessages((ms) => [...(followUp ? ms : []),
      { role: "user", text: label, chip: chip || "", question: chip ? "" : text, client: client || "", at: new Date().toISOString(), fresh: true, deep },
      { role: "assistant", text: "", steps: [], running: true, fresh: true }]);
    setTimeout(() => turnRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    try {
      abortRef.current = new AbortController();
      const res = await fetch(`${API}/ai/chat`, {
        signal: abortRef.current.signal,
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("rl_token")}` },
        body: JSON.stringify({ message: text, chip: chip || "", client: client || "", deep, conversation_id: followUp ? conv.id : null }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        patchLast(() => ({ role: "assistant", error: true, text: d.detail || "Analyst couldn't answer just now." }));
        return;
      }
      for await (const ev of readSse(res)) {
        if (ev.type === "start") {
          newConvId = ev.conversation_id;
          setConv((c) => ({ ...(followUp ? c : {}), id: ev.conversation_id, title: ev.title, audience: ev.audience, client: ev.client }));
        } else if (ev.type === "step") {
          patchLast((m) => ({ ...m, steps: [...(m.steps || []), { text: ev.text }] }));
        } else if (ev.type === "data") {
          setConv((c) => ({ ...c, as_of: ev.as_of, counts: ev.counts }));
          patchLast((m) => ({ ...m, steps: [...(m.steps || []), { text: ev.text }] }));
        } else if (ev.type === "route") {
          patchLast((m) => ({ ...m, route: { model: ev.model, reason: ev.reason, commentary: ev.commentary }, deepSuggested: !!ev.deep_suggested }));
        } else if (ev.type === "delta") {
          patchLast((m) => ({ ...m, text: (m.text || "") + ev.text }));
        } else if (ev.type === "done") {
          patchLast((m) => ({ ...m, running: false, cost: ev.cost_usd, codeRuns: ev.code_runs, deepSuggested: !!ev.deep_suggested }));
          setOverview((o) => (o ? { ...o, remaining: ev.remaining } : o));
          refreshOverview();
        } else if (ev.type === "error") {
          patchLast(() => ({ role: "assistant", error: true, text: ev.message }));
        }
      }
    } catch (e) {
      if (e?.name === "AbortError") {
        patchLast((m) => ({ ...m, running: false, stopped: true }));
      } else {
        patchLast(() => ({ role: "assistant", error: true, text: "Connection lost. Try again." }));
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
      patchLast((m) => ({ ...m, running: false }));
      refreshLists();
      setTimeout(refreshOverview, 800);
    }
    return newConvId;
  };

  const openConversation = async (id) => {
    if (busy) return;
    try {
      const { data } = await axios.get(`${API}/ai/conversations/${id}`);
      setConv({ id: data.id, title: data.title, audience: data.audience || "internal", client: data.client || "", as_of: data.as_of, counts: data.counts });
      toTop();
      setMessages((data.messages || []).map((m) => (m.role === "user"
        ? { role: "user", text: m.text, chip: m.chip || "", question: m.question || "", at: m.at, deep: !!m.deep }
        : { role: "assistant", text: m.text, cost: m.cost_usd, codeRuns: m.code_runs, deepSuggested: !!m.deep_suggested, route: m.model ? { model: m.model, reason: m.route_reason } : null, steps: [] })));
    } catch { toast.error("Couldn't open that conversation"); }
  };

  const saveReport = async (i, originEl) => {
    const q = messages[i - 1];
    if (!q) return false;
    const body = q.chip
      ? { title: q.text, chip: q.chip, client: conv?.client || "", audience: conv?.audience || "internal" }
      : { title: q.text.slice(0, 80), message: q.question || q.text, audience: "internal" };
    body.conversation_id = conv?.id || "";
    try {
      const { data } = await axios.post(`${API}/ai/reports`, body);
      await flyTo(originEl, '[data-testid="analyst-saved-list"]');
      setFreshReportId(data.id);
      setTimeout(() => setFreshReportId((id) => (id === data.id ? null : id)), 3200);
      await refreshLists();
      if (!document.querySelector('[data-testid="analyst-saved-list"]')?.offsetParent) toast.success("Saved — find it under Saved reports");
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the report"); return false; }
  };

  const rerunReport = async (r) => {
    if (busy) return;
    setRerunId(r.id);
    try {
      const id = await run(r.chip ? { chip: r.chip, client: r.client } : { message: r.message });
      if (id) await axios.patch(`${API}/ai/reports/${r.id}`, { conversation_id: id }).catch(() => {});
      await refreshLists();
    } finally { setRerunId(null); }
  };
  // Opening a saved report shows the answer it saved; old reports without one re-run.
  const openReport = (r) => (r.conversation_id ? openConversation(r.conversation_id) : rerunReport(r));
  const runReport = openReport;
  const deleteReport = async (r) => {
    try { await axios.delete(`${API}/ai/reports/${r.id}`); refreshLists(); } catch { toast.error("Couldn't delete"); }
  };
  const deleteConv = async (c) => {
    try {
      await axios.delete(`${API}/ai/conversations/${c.id}`);
      if (conv?.id === c.id) newQuestion();
      refreshLists();
    } catch { toast.error("Couldn't delete"); }
  };

  const disabledReason = useMemo(() => {
    if (!overview) return "";
    if (overview.error) return overview.error;
    if (!overview.configured) return "AI is not set up — the Anthropic key is missing";
    if (overview.company_cap_reached) return "Company AI limit reached for today";
    if ((overview.remaining ?? 1) <= 0) return "No questions left today — resets at midnight IST";
    return "";
  }, [overview]);

  // "Think deeper with Opus": stop the running Sonnet answer (only what ran is billed)
  // and re-ask the same question on Opus in this conversation.
  const [pendingDeep, setPendingDeep] = useState(null);
  const questionOf = (i) => { const u = messages[i - 1]; return u ? (u.question || u.text) : ""; };
  const goDeep = (q) => { if (!q) return; if (busy) { setPendingDeep(q); stop(); } else run({ message: q, deep: true }); };
  useEffect(() => {
    if (!busy && pendingDeep) { const q = pendingDeep; setPendingDeep(null); run({ message: q, deep: true }); }
  }, [busy, pendingDeep]); // eslint-disable-line react-hooks/exhaustive-deps
  const rate = overview?.inr_per_usd;
  const round5 = (v) => Math.max(5, Math.round(v / 5) * 5);
  const deepRange = rate ? `usually ₹${round5(0.25 * rate)}–${round5(0.45 * rate)}` : "usually $0.25–0.45";
  const deepRangeShort = deepRange.replace("usually ", "~");

  // While an answer is being worked out, the model sits under the send button.
  const lastMsg = messages[messages.length - 1];
  const liveStatus = busy && lastMsg?.route ? {
    name: (MODEL_LOOK[lastMsg.route.model] || MODEL_LOOK.sonnet).name,
    note: (lastMsg.route.commentary || COMMENTARY[lastMsg.route.model] || "").replace(/^Using \w+ — /, "").replace(/\.$/, ""),
    deepOffer: lastMsg.deepSuggested ? { range: deepRangeShort, reason: lastMsg.route.reason, onClick: () => goDeep(questionOf(messages.length - 1)) } : null,
  } : null;

  const left = overview && !overview.error ? `${overview.remaining} of ${overview.daily_limit} questions left today` : "";
  const landingLine = overview && !overview.error
    ? `${fmt(overview.lines)} lines${overview.events != null ? ` · ${fmt(overview.events)} history events` : " · full history"} · live data${left ? ` · ${left}` : ""}`
    : "Live RockLink data";
  const lastUser = messages.map((m) => m.role).lastIndexOf("user");

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 max-w-[1280px] mx-auto" data-testid="analyst-page">
      <LeftColumn reports={reports} convs={convs} activeId={conv?.id} freshReportId={freshReportId} rerunId={rerunId} overview={overview} onLanding={!conv && messages.length === 0}
        onNew={newQuestion} onOpenReport={openReport} onRerunReport={rerunReport}
        onDeleteReport={deleteReport} onOpen={openConversation} onDeleteConv={deleteConv} busy={busy} />

      <div className="flex-1 min-w-0 max-w-[920px]">
        {!conv && messages.length === 0 ? (
          <div className="space-y-6 an-in" data-testid="analyst-landing">
            {disabledReason && overview?.error && (
              <div className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[13px] text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-4 h-4" />{disabledReason}
              </div>
            )}
            <AskBox big value={input} onChange={setInput} onSend={() => run({ message: input })} onStop={stop} busy={busy}
              disabledReason={disabledReason} dataLine={landingLine}
              placeholder="Ask anything — e.g. “Which TASL lines shipped late in September and why?”" />
            <Suggestions chips={chips} clients={clients} onRun={run} disabled={busy || !!disabledReason} />
            <section className="lg:hidden">
              <h2 className="flex items-center gap-1.5 text-[12px] font-semibold an-muted mb-2"><Bookmark className="w-4 h-4" />Your saved reports</h2>
              {reports.length === 0
                ? <p className="text-[12px] an-muted">Save any answer as a report — it re-runs on fresh data when you open it.</p>
                : <div className="flex flex-wrap gap-2">{reports.map((r) => (
                    <button key={r.id} type="button" onClick={() => runReport(r)} className="rl-tile inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60">
                      <Bookmark className="w-3.5 h-3.5 text-orange-500" />{r.title}
                    </button>))}</div>}
            </section>
          </div>
        ) : (
          <div className="space-y-10" data-testid="analyst-conversation">
            {messages.map((m, i) => {
              if (m.role === "user") {
                return (
                  <div key={i} ref={i === lastUser ? turnRef : null}
                    className={`scroll-mt-4 ${i > 0 ? "pt-8 border-t border-[hsl(var(--border))]/70" : ""}`}>
                    <TurnHeader m={m} conv={conv} showAudience={i === 0} />
                  </div>
                );
              }
              return (
                <div key={i} className="-mt-6 space-y-4" data-testid="analyst-assistant-msg">
                  {m.error ? (
                    <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/5 px-3.5 py-2.5 text-[13px] text-red-700 dark:text-red-300" data-testid="analyst-error">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{m.text}
                    </div>
                  ) : (
                    <>
                      {m.stopped && (
                        <p className="flex items-center gap-1.5 text-[12px] an-muted" data-testid="analyst-stopped">
                          <Square className="w-3 h-3 fill-current" />Stopped by you — only the work done so far is charged.
                        </p>
                      )}
                      {m.running
                        ? <WorkList steps={m.steps} hasText={!!m.text} />
                        : <MetaLine conv={conv} m={m} fresh={m.fresh} inrPerUsd={overview?.inr_per_usd} />}
                      {m.text && (
                        <AnswerView text={m.text} streaming={m.running} animate={!!m.fresh} title={conv?.title || "RockLink report"} asOf={conv?.as_of}
                          audience={conv?.audience || "internal"} client={conv?.client}
                          onSave={(el) => saveReport(i, el)} />
                      )}
                      {!m.running && !m.stopped && m.deepSuggested && !messages.slice(i + 1).some((x) => x.deep) && (
                        <DeepOffer cost={m.cost != null && rate ? inr(Number(m.cost), rate) : ""} range={deepRange}
                          busy={busy} onDeep={() => goDeep(questionOf(i))} />
                      )}
                    </>
                  )}
                </div>
              );
            })}
            <div className="pt-2">
              <AskBox value={input} onChange={setInput} onSend={() => run({ message: input })} onStop={stop} busy={busy}
                disabledReason={disabledReason} dataLine={left || "Ask a follow-up"} status={liveStatus}
                placeholder={conv?.audience === "customer" ? "Ask a follow-up — stays customer-safe" : "Ask a follow-up…"} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
